// Firebase v9's compat packages retain the namespaced API used throughout
// this Vue 2 application, while adding the Enterprise App Check provider.
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/functions'
import firebaseConfigProd from './firebaseConfigProd'
import firebaseConfigAcc from './firebaseConfigAcc'

// Region for the callable functions used by this frontend (createSubmission,
// getResult). The existing v1 mail functions stay in us-central1 and are
// not called directly from the browser.
const FUNCTIONS_REGION = 'europe-west1';

const host = window.location.hostname;
const isProd = host === 'www.testjewerkgeluk.nl';
// Local development uses the Firebase emulators and cannot access acceptance
// unless a developer explicitly starts a one-off App Check acceptance test.
const isLocalDev = host === 'localhost' || host === '127.0.0.1' || host.indexOf('.ddev.site') !== -1;
// This opt-in mode is reserved for testing acceptance App Check itself. Normal
// local development keeps all Auth and Functions traffic within the emulators.
const appCheckDebugToken = process.env.APP_CHECK_DEBUG_TOKEN;
const isAccLiveAppCheck = isLocalDev && process.env.APP_CHECK_ACC_LIVE === true && Boolean(appCheckDebugToken);

if (isLocalDev && process.env.APP_CHECK_ACC_LIVE === true && !appCheckDebugToken) {
  throw new Error('APP_CHECK_DEBUG_TOKEN ontbreekt. Voeg een geregistreerde acceptatie-debugtoken toe aan .env.');
}

const firebaseConfig = isAccLiveAppCheck
  ? firebaseConfigAcc
  : (isLocalDev
  ? { apiKey: 'demo-local-key', projectId: 'demo-testjewerkgeluk' }
  : (isProd ? firebaseConfigProd : firebaseConfigAcc));

if (isAccLiveAppCheck) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken;
}

// The Firebase compat App Check module reads FIREBASE_APPCHECK_DEBUG_TOKEN
// while it is evaluated. It must therefore load only after the local debug
// token is set; a static import would run too early and silently select
// reCAPTCHA.
require('firebase/compat/app-check');

const firebaseApp = firebase.initializeApp(firebaseConfig);
if (!isLocalDev || isAccLiveAppCheck) {
  // Callable requests include this token automatically. The v2 creation
  // callable enforces it outside the local emulator suite.
  firebaseApp.appCheck().activate(
    new firebase.appCheck.ReCaptchaEnterpriseProvider(firebaseConfig.appCheckSiteKey),
    true
  );
}

const auth = firebaseApp.auth();
const functions = firebaseApp.functions(FUNCTIONS_REGION);

if (isLocalDev && !isAccLiveAppCheck) {
  // The emulator suite is part of this DDEV project. A bare loopback address
  // isn't reliably reachable from every host/browser setup, while the DDEV
  // router hostname is. This app itself is served over HTTPS in local dev too,
  // so the emulator connections must be HTTPS as well — otherwise the browser
  // blocks them as mixed content. Firebase 9.3's public Functions emulator
  // APIs always rebuild the endpoint as http://, including the deprecated URL
  // overload. Set the compat delegate's emulator origin directly so its normal
  // callable URL builder retains the HTTPS origin exposed by the DDEV router.
  const EMULATOR_HOST = 'testjewerkgeluk.ddev.site';
  auth.useEmulator('https://' + EMULATOR_HOST + ':19099');
  functions._delegate.emulatorOrigin = 'https://' + EMULATOR_HOST + ':15001';
}

// The participant fills in the quiz without a visible login screen: an
// anonymous session is started as soon as the app loads, and every
// createSubmission call waits for this. Reading a result link never needs
// it (a shared link works without the original session).
//
// This is a function, not a single cached promise: if the initial sign-in
// fails (e.g. a transient network error), a later retry must actually
// attempt sign-in again instead of forever re-resolving the same
// permanently-rejected promise, which would make the "opnieuw proberen"
// retry button on a failed submission unable to ever succeed.
function authReady() {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }
  return auth.signInAnonymously().catch((error) => {
    console.error('Anonieme aanmelding is mislukt:', error);
    throw error;
  });
}

authReady();

export { auth, functions, authReady };



// WEBPACK FOOTER //
// ./src/components/firebaseInit.js